<template>
  <base-select-new
    :items="items"
    :value="value"
    :multiple="multiple"
    :can-unselect="canUnselect"
    :value-field="valueField"
    :title-field="titleField"
    :placeholder="placeholder"
    :should-hide-selected-value="shouldHideSelectedValue"
    :can-have-empty-value="canHaveEmptyValue"
    :disabled="disabled"
    :open-triggers="openTriggers"
    :list-view="listView"
    :is-text-mode="isTextMode"
    :class="selectClasses"
    class="ui-select"
    @input="$emit('input', $event)"
    @open="openItems"
    @close="closeItems">
    <!--  List View  -->
    <template
      v-if="listView"
      #list="{ selectItem, availableItems, selectedIndexes }">
      <div
        v-if="label"
        class="ui-select-list__title">
        {{ label }}
      </div>
      <ul
        v-if="availableItems"
        class="ui-select-list">
        <li
          v-for="(item, index) in availableItems"
          :key="getItemKey(item)"
          :class="{'ui-select-list__item--active': selectedIndexes.includes(index) }"
          class="ui-select-list__item transition--color"
          @click.stop="selectItem(item)">
          <span
            v-if="item.image"
            :style="getImageMaskStyle(item.image)"
            class="ui-select-list__item-img transition--color-bg" />
          {{ item[titleField] || item }}
        </li>
      </ul>
    </template>

    <!--  Select View  -->
    <template
      v-if="!listView"
      #value="{ selectItem, selectedItems }">
      <div
        class="ui-select__head transition--color-bg"
        :class="{ 'ui-select__head--disabled': disabled }">
        <div
          v-if="label"
          class="ui-select__label">
          {{ label }}
        </div>
        <div class="ui-select__head-content">
          <div
            ref="valueRow"
            :class="{ 'ui-select__value--multiple': multiple }"
            class="ui-select__value transition--color">
            <template v-if="multiple">
              <template v-if="selectedItems.length">
                <div
                  v-for="item in selectedItems"
                  :key="`value-item-${getItemKey(item)}`"
                  ref="valueItem"
                  class="ui-select__value-item">
                  <slot
                    :selected-item="item"
                    name="value">
                    <template v-if="!emptyTitleField">
                      {{ item[titleField] || item }}
                    </template>
                  </slot>
                  <div
                    v-if="!hideMultipleCross"
                    class="shape-cross"
                    @click.stop="selectItem(item)" />
                </div>
                <div
                  v-if="overflowedValuesCount"
                  class="ui-select__value--overflow">
                  + {{ overflowedValuesCount }} more
                </div>
              </template>
              <template v-else>
                {{ placeholder }}
              </template>
            </template>
            <template v-else-if="selectedItems[0]">
              <slot
                :selected-item="selectedItems[0]"
                name="value">
                <template v-if="selectedItems[0].image">
                  <span
                    v-if="svgMask"
                    :style="getImageMaskStyle(selectedItems[0].image)"
                    class="ui-select-list__item-img ui-select-list__item-img--mask transition--color-bg" />
                  <img
                    v-else
                    :src="selectedItems[0].image"
                    class="ui-select__item-img"
                    height="24px"
                    width="24px">
                </template>
                <span
                  v-if="isOpen && selectedItems[0].prefix"
                  class="ui-select__item-prefix">
                  {{ selectedItems[0].prefix }}
                </span>
                <template v-if="!emptyTitleField">
                  {{ titleField ? selectedItems[0][titleField] : selectedItems[0] }}
                </template>
                <span
                  v-if="subtitleField"
                  class="ui-select__subtitle">
                  {{ selectedItems[0][subtitleField] }}
                </span>
              </slot>
            </template>
            <template v-else>
              {{ placeholder }}
            </template>
          </div>
          <error-tooltip :error="error" />
          <div class="ui-select__arrow-wrap">
            <arrowhead-icon
              :is-reverse="isOpen"
              class="ui-select__arrow" />
          </div>
        </div>
      </div>
    </template>
    <template
      v-if="!listView"
      #options="{ availableItems, selectedItems, selectItem, isOverflowed, closeItems }">
      <slot
        :selected-items="selectedItems"
        :close-items="closeItems"
        :available-items="availableItems"
        :select-item="selectItem"
        name="options">
        <div
          :class="{ 'ui-select__items-inner--overflowed': isOverflowed }"
          class="ui-select__items-inner transition--color-bg">
          <div class="ui-select__separator" />
          <div
            ref="items"
            class="ui-select__items__wrapper">
            <div
              v-for="item in availableItems"
              :key="`items-item-${getItemKey(item)}`"
              class="ui-select__items-item transition--color"
              :class="{'ui-select__items-item--active': selectedItems.includes(valueField ? item[valueField] : item) }"
              @click.stop="selectItem(item)">
              <template v-if="item.image">
                <span
                  v-if="svgMask"
                  :style="getImageMaskStyle(item.image)"
                  class="ui-select-list__item-img ui-select-list__item-img--mask transition--color-bg" />
                <img
                  v-else
                  :src="item.image"
                  class="ui-select__item-img"
                  height="24px"
                  width="24px">
              </template>
              <div>
                <span
                  v-if="item.prefix"
                  class="ui-select__item-prefix">
                  {{ item.prefix }}
                </span>
                <template v-if="!emptyTitleField">
                  {{ item[titleField] || item }}
                </template>
                <span
                  v-if="subtitleField"
                  class="ui-select__subtitle">
                  {{ item[subtitleField] }}
                </span>
              </div>
              <div
                v-if="shouldShowCheckShape"
                class="shape-check"
                :class="{ 'shape-check--hidden': valueField
                  ? !selectedItems.map((i) => i[valueField]).includes(item[valueField] || item)
                  : !selectedItems.includes(item) }" />
            </div>
            <span
              v-if="hasScroll"
              ref="scrollShadow"
              class="ui-select__items-gradient-wrapper transition--opacity">
              <span class="ui-select__items-gradient" />
            </span>
          </div>
        </div>
      </slot>
    </template>
  </base-select-new>
</template>

<script>
import BaseSelectNew from 'Components/ui/Select/BaseSelectNew.vue';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';

let scrollIntersectionObserver = null;

export default {
  name: 'UiKitSelect',

  components: {
    BaseSelectNew,
    ArrowheadIcon,
    ErrorTooltip,
  },

  props: {
    items: {
      type: Array,
      required: true,
    },

    value: {
      type: [String, Number, Array, Object],
      default: () => null,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    valueField: {
      type: String,
      default: null,
    },

    shouldHideSelectedValue: {
      type: Boolean,
      default: false,
    },

    canHaveEmptyValue: {
      type: Boolean,
      default: false,
    },

    canUnselect: {
      type: Boolean,
      default: true,
    },

    shouldShowCheckShape: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: null,
    },

    inlineLabel: {
      type: Boolean,
      default: false,
    },

    titleField: {
      type: String,
      default: 'title',
    },

    placeholder: {
      type: String,
      default: null,
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    emptyTitleField: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    subtitleField: {
      type: String,
      default: null,
    },

    openTriggers: {
      type: Array,
      default: () => ['click'],
    },

    hideMultipleCross: {
      type: Boolean,
      default: false,
    },

    isTextMode: {
      type: Boolean,
      default: false,
    },

    listView: {
      type: Boolean,
      default: false,
    },

    svgMask: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hasScroll: false,
      isOpen: false,
      overflowedValuesCount: 0,
      valuesIntersectionObserver: null,
    };
  },

  computed: {
    selectClasses() {
      const selectViewClasses = {
        'ui-select--open': this.isOpen,
        'ui-select--has-label': this.label && !this.inlineLabel,
        'ui-select__label--inline': this.inlineLabel,
      };

      const listViewClasses = {
        'ui-select-list__wrapper': this.listView,
      };
      return this.listView ? listViewClasses : selectViewClasses;
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length) {
          this.$nextTick(() => {
            this.initValuesIntersection(this.$refs.valueItem);
          });
        }
      },
      immediate: true,
    },
  },

  mounted() {
    if (this.multiple) {
      this.valuesIntersectionObserver = new IntersectionObserver((entries) => {
        let i = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            i += 1;
          }
        });
        this.overflowedValuesCount = i;
      }, {
        root: this.$refs.valuesRow,
        threshold: 0.9,
        rootMargin: '0px',
      });
    }
  },

  methods: {
    initValuesIntersection(values) {
      if (!values) {
        return;
      }

      this.valuesIntersectionObserver.disconnect();
      values.forEach((node) => {
        this.valuesIntersectionObserver.observe(node);
      });
    },

    initScrollIntersection() {
      const lastItem = this.$refs.items.querySelector('.ui-select__items-item:last-of-type');

      scrollIntersectionObserver = new IntersectionObserver(([entry]) => {
        requestAnimationFrame(() => {
          if (entry.isIntersecting) {
            this.$refs.scrollShadow.style.opacity = 0;
          } else {
            this.$refs.scrollShadow.style.opacity = 1;
          }
        });
      }, {
        root: this.$refs.items,
        threshold: 0.5,
        rootMargin: '0px',
      });

      scrollIntersectionObserver.observe(lastItem);
    },

    destroyScrollIntersection() {
      scrollIntersectionObserver = null;
    },

    openItems() {
      this.isOpen = true;
      if (this.$refs.items) {
        this.$nextTick(() => {
          const itemsStyle = getComputedStyle(this.$refs.items);
          this.hasScroll = parseInt(itemsStyle.height, 10) >= parseInt(itemsStyle.maxHeight, 10);

          if (this.hasScroll) {
            this.initScrollIntersection();
          }
        });
      }
      this.$emit('open');
    },

    closeItems() {
      this.isOpen = false;
      this.destroyScrollIntersection();
      this.$emit('close');
    },

    getItemKey(item) {
      if (typeof item !== 'object') {
        return item;
      }

      let key = '';
      if (item.id) {
        key = item.id;
      }

      if (this.valueField && typeof item[this.valueField] !== 'object') {
        key = `${key}${item[this.valueField]}`;
      }

      if (this.titleField) {
        key = `${key}${item[this.titleField]}`;
      }

      return key;
    },

    getImageMaskStyle(url) {
      return `mask: url(${url}) no-repeat center/contain;
      -webkit-mask: url(${url}) no-repeat center/contain`;
    },
  },
};
</script>

<style lang="scss" scoped>
  .ui-select {
    --bg-color: #{$color--basic-menu};
    --bg-color--open: #{$color--basic-dark-gray-hsl};
    --bg-color-head: var(--bg-color);
    --bg-color-head--open: var(--bg-color--open);
    --bg-color-elements: #{$color--basic-dark-gray};
    --bg-color-mask-img: #{$color--basic-white};
    --head-elements-color: #{$color--basic-dark-gray};
    --head-elements-color--open: #{$color--basic-light-gray};
    --elements-color: #{$color--basic-light-gray};
    --elements-color--active: #{$color--basic-white};
    --elements-bg--active: transparent;
    --check-color: #{$color--basic-green};
    --border-radius: 3px;
    --font-size: #{$text-13px};
    --font-size--with-label: #{$text-15px};
    --font-weight: 400;
    --items-max-height: 300px;
    --height: 40px;
    --height--with-title: 60px;
    --img-size: 24px;
    --head-color: #{$color--basic-white};
    --head-color--open: #{$color--basic-white};
    --subtitle-color: #{$color--basic-light-gray};

    font-size: var(--font-size);
  }

  .ui-select--open {
    --z-index: 1000;

    .ui-select__head {
      background-color: hsl(var(--bg-color-head--open));
    }

    .ui-select__items-inner {
      background-color: var(--bg-color-elements);
    }

    .ui-select__arrow {
      --shape-color: var(--head-elements-color--open);
    }

    .ui-select__label {
      color: var(--head-elements-color--open);
      border-right-color: var(--head-elements-color--open);
    }

    .ui-select__value {
      color: var(--head-color--open);
    }
  }

  .ui-select--has-label {
    .ui-select__value {
      font-size: var(--font-size--with-label);
      height: calc(var(--font-size--with-label) * 1.4);
    }

    .ui-select__head {
      height: var(--height--with-title);
    }
  }

  .ui-select__label--inline {
    .ui-select__label {
      font-size: $text-11px;
      text-transform: uppercase;
      font-weight: 500;
      margin-bottom: 0;
      padding: 4px 12px 4px 0;
      margin-right: 12px;
      border-right: 1px solid var(--head-elements-color);
      color: var(--head-elements-color);
    }

    .ui-select__head {
      flex-direction: row;
      align-items: center;
    }

    .ui-select__arrow {
      --shape-color: var(--head-elements-color);
    }
  }

  .ui-select__label {
    color: var(--head-elements-color--open);
    font-size: $text-13px;
    margin-bottom: 6px;
    transition: var(--transition-color-time) var(--transition-color-easing);
    transition-property: color, border-right-color;
  }

  .ui-select__separator {
    height: 1px;
    background-color: var(--head-elements-color--open);
    position: absolute;
    top: 0;
    left: 13px;
    width: calc(100% - 26px);
  }

  .ui-select__arrow-wrap {
    width: 18px;
    flex-shrink: 0;
  }

  .ui-select__arrow {
    --shape-color: var(--head-elements-color--open);
  }

  .ui-select__value {
    flex-grow: 1;
    font-size: var(--font-size);
    font-weight: var(--font-weight);
    min-height: 24px;
    height: calc(var(--font-size) * 1.4);
    font-weight: var(--font-weight);
    line-height: 1.4;
    overflow-y: hidden;
    display: flex;
    align-items: center;
    color: var(--head-color);

    &--multiple  {
      margin-right: 30px;
      display: block;
      align-items: initial;
      min-height: initial;
    }
  }

  .ui-select__value-item {
    display: inline-flex;
    align-items: center;
    margin-right: 20px;

    .shape-cross {
      --shape-color: #{$color--basic-light-gray};
      width: 12px;
      margin-left: 5px;

      &:hover {
        --shape-color: #{$color--basic-white};
      }

      &:before,
      &:after {
        transition: var(--transition-color-bg);
      }
    }
  }

  .ui-select__head {
    height: var(--height);
    padding: 0 13px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: var(--bg-color-head);
    cursor: pointer;
    border-radius: var(--border-radius);
  }

  .ui-select__head--disabled {
    cursor: not-allowed;
  }

  .ui-select__head-content {
    display: flex;
    width: 100%;
    position: relative;
    align-items: center;
  }

  .ui-select__value--overflow {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--head-elements-color--open);
    font-weight: 500;
  }

  .ui-select__items-inner {
    background-color: var(--bg-color-elements);
    border-radius: 0 0 var(--border-radius) var(--border-radius);
    position: relative;
    padding: 8px 0;
    transform: translateY(calc(-1 * var(--border-radius)));
  }

  .ui-select__items-inner--overflowed {
    transform: translateY(-100%) translateY(calc(-1 * var(--height))) translateY(var(--border-radius));
    border-radius: var(--border-radius) var(--border-radius) 0 0;

    .ui-select__separator {
      top: unset;
      bottom: 0;
    }
  }

  .ui-select__items__wrapper {
    --scroll-track-color: transparent;
    --scroll-thumb-color: #{rgba($color--basic-light-gray, .31)};

    max-height: var(--items-max-height);
    position: relative;
    overflow-y: auto;
  }

  .ui-select__items-item {
    display: flex;
    align-items: center;
    color: var(--elements-color);
    cursor: pointer;
    line-height: 1.3;
    padding: 6px 13px;

    .shape-check {
      width: 12px;
      height: 9px;
      margin-left: 5px;
      --shape-color: var(--check-color);
    }

    &.ui-select__items-item--active {
      color: var(--elements-color--active);
      background-color: var(--elements-bg--active);
    }

    &:hover {
      color: var(--elements-color--active);
    }
  }

  .ui-select__items-gradient-wrapper {
    display: block;
    position: sticky;
    bottom: 0;
    opacity: 1;
  }

  .ui-select__items-gradient {
    border-radius: 0 0 var(--border-radius) var(--border-radius);
    position: absolute;
    left: -1px; // иногда буквы вылетают за градиент слева О_О
    bottom: 0;
    width: calc(100% + 1px);
    height: 24px;
    pointer-events: none;
    background-image: linear-gradient(
        to bottom,
        hsla(var(--bg-color-head--open), 0) 27%,
        hsl(var(--bg-color-head--open))
    );
  }

  .ui-select__item-img {
    height: var(--img-size);
    width: var(--img-size);
    margin-right: 8px;
    border-radius: 50%;
    object-fit: cover;
  }

  .ui-select__item-prefix {
    margin-right: 5px;
    color: $color--basic-white;
  }

  .ui-select__subtitle {
    color: var(--subtitle-color);
    margin-left: 6px;
  }

  //  list view
  .ui-select-list__wrapper {
    background-color: var(--bg-color);
    border-radius: var(--border-radius);
    padding: 24px;
    font-weight: 500;
  }

  .ui-select-list__title {
    text-transform: uppercase;
    font-size: var(--font-size--with-label);
    line-height: 1.15;
    margin-bottom: 16px;
    letter-spacing: 0.1em;
    color: $color--basic-dark-gray;
  }

  .ui-select-list__item {
    display: flex;
    align-items: center;
    font-size: var(--font-size);
    line-height: 1.15;
    cursor: pointer;
    color: var(--elements-color);

    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  .ui-select-list__item-img {
    margin-right: 8px;
    width: var(--img-size);
    height: var(--img-size);
    background-color: var(--elements-color);
    flex-shrink: 0;
  }

  .ui-select-list__item:hover,
  .ui-select-list__item--active {
    color: var(--elements-color--active);

    .ui-select-list__item-img {
      background-color: var(--elements-color--active);
    }
  }

  .ui-select-list__item-img--mask {
    background-color: var(--bg-color-mask-img);
  }
</style>
